import { CreditCard, LogOut, TriangleAlert } from "lucide-react";
import { useLocaleState, useLogout, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";

import type { BillingState } from "./billingState";
import { useBillingActions, useBillingStatus } from "./useBilling";

const formatDate = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(date);

const MESSAGES = {
  activate: {
    text: "crm.billing.banner.activate",
    button: "crm.billing.activate",
  },
  unpaid: { text: "crm.billing.banner.unpaid", button: "crm.billing.pay" },
  ended: { text: "crm.billing.banner.ended", button: "crm.billing.reactivate" },
} as const;

/** The strip under the top bar while the subscription is not paid. */
export const BillingBanner = ({ state }: { state: BillingState }) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { pay, isOpening } = useBillingActions();
  if (state.kind === "ok" || state.kind === "blocked") return null;

  const messages = MESSAGES[state.kind];
  return (
    <div role="status" className="bg-destructive text-white">
      <div className="max-w-screen-xl mx-auto flex flex-wrap items-center gap-3 px-4 py-2 text-sm font-medium">
        {state.kind === "activate" ? (
          <CreditCard className="size-4 shrink-0" />
        ) : (
          <TriangleAlert className="size-4 shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          {translate(messages.text, {
            date: "deadline" in state ? formatDate(state.deadline, locale) : "",
          })}
        </span>
        <Button
          size="sm"
          variant="secondary"
          className="bg-white text-destructive hover:bg-white/90"
          disabled={isOpening}
          onClick={pay}
        >
          {translate(messages.button)}
        </Button>
      </div>
    </div>
  );
};

/** In place of the whole CRM once the grace period is over. */
export const BillingBlocked = () => {
  const translate = useTranslate();
  const logout = useLogout();
  const { pay, isOpening } = useBillingActions();
  const { refetch, isFetching } = useBillingStatus();
  return (
    <main className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="max-w-md w-full rounded-lg border bg-card p-6 flex flex-col gap-4">
        <TriangleAlert className="size-8 text-amber-600" />
        <h1 className="text-xl font-semibold">
          {translate("crm.billing.blocked.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.billing.blocked.text")}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button disabled={isOpening} onClick={pay}>
            <CreditCard className="size-4" />
            {translate("crm.billing.pay")}
          </Button>
          <Button
            variant="outline"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            {translate("crm.billing.blocked.paid")}
          </Button>
          <Button variant="ghost" onClick={() => logout()}>
            <LogOut className="size-4" />
            {translate("ra.auth.logout")}
          </Button>
        </div>
      </div>
    </main>
  );
};
